import {type FormEventHandler} from 'react';
import {FormattedMessage} from 'react-intl';
import {Dialog, type DialogProps, DialogTitle, DialogContent, Grid, Typography, MenuItem} from '@mui/material';
import {DocumentType} from 'types';
import {useCacheReadQuery, useFormState} from 'hooks';
import {useSelectedLinkContext} from 'contexts';
import {GET_BOOKMARK_FOLDERS_QUERY, type GetBookmarkFoldersOutput} from 'graphql/admin/bookmarks';
import {SaveButton, ElevatedInput, ElevatedSelect, Alert, Placeholder} from 'components';

type EditBookmarkDialogProps = DialogProps & {
  handleEditBookmark: Function;
  submitting: boolean;
  bookmarkName: string;
  bookmarkUrl: string;
  bookmarkFolderKey?: string;
  errorReason?: string;
}

const EditBookmarkDialog = ({bookmarkName, bookmarkUrl, bookmarkFolderKey = '', open, onClose, handleEditBookmark, submitting, errorReason} : EditBookmarkDialogProps) : JSX.Element => {
  const { selectedLink: { channelKey: channel } } = useSelectedLinkContext();
  const foldersData = useCacheReadQuery<GetBookmarkFoldersOutput>({
    query: GET_BOOKMARK_FOLDERS_QUERY,
    variables: {
      adminFoldersByFolderTypeInput: {
        channel,
        folderType: DocumentType.BOOKMARK
      }
    }
  });
  const folders = foldersData?.adminFoldersByFolderType?.folders || [];

  const [values, onChange] = useFormState({
    newBookmarkName: bookmarkName,
    newUrl: bookmarkUrl,
    newFolderKey: bookmarkFolderKey
  });

  const handleSubmit : FormEventHandler<HTMLFormElement> = event => {
    event.preventDefault();
    if (newBookmarkName === bookmarkName && newUrl === bookmarkUrl && newFolderKey === bookmarkFolderKey) onClose(event, "backdropClick");
    else handleEditBookmark(values);
  }

  const renderValue = (value : string) => folders.find(({key}) => key === value)?.name || <Placeholder>{bookmarkFolderKey ? 'Aucun dossier' : "Ce favori n'appartient à aucun dossier"}</Placeholder>;

  const {newBookmarkName, newUrl, newFolderKey} = values;
  
  return <Dialog open={open} onClose={onClose} fullWidth>
    <form onSubmit={handleSubmit}>
      <DialogTitle>
        <Grid container justifyContent='space-between' alignItems='center' wrap="nowrap">
          <Grid item>
            <Grid  container alignItems='center'>
              <Typography variant='h6' color='primary'>
                <FormattedMessage id='admin.bookmarks.update.title' values={{ bookmarkName }} />
              </Typography>
            </Grid>
          </Grid>
          <Grid item>
            <SaveButton className="bg-admin-blue-300" type='submit' loading={submitting} />
          </Grid>
        </Grid>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={4}>
          <Grid item xxs={12}>
            <ElevatedInput
              label='Nom'
              name='newBookmarkName'
              value={newBookmarkName}
              onChange={onChange}
              required
              fullWidth
              autoFocus
            />
          </Grid>
          <Grid item xxs={12}>
            <ElevatedInput
              label='Adresse URL du site internet'
              name='newUrl'
              value={newUrl}
              onChange={onChange}
              required
              fullWidth
            />
          </Grid>
          <Grid item xxs={12}>
            <ElevatedSelect
              label='Dossier'
              name='newFolderKey'
              value={newFolderKey}
              onChange={onChange}
              renderValue={renderValue}
              fullWidth
              displayEmpty
            >
              {[
                <MenuItem key='none' value=''>
                  <Placeholder>Aucun dossier</Placeholder>
                </MenuItem>,
                ...folders.map(({key, name}) => (
                  <MenuItem key={key} value={key}>
                    {name}
                  </MenuItem>
                ))
              ]}
            </ElevatedSelect>
          </Grid>
        </Grid>
      {errorReason && <Alert>
        <Typography><FormattedMessage id={'admin.bookmarks.action.failed.' + errorReason} defaultMessage={errorReason} /></Typography>
      </Alert>}
      </DialogContent>
    </form>
  </Dialog>
}

export default EditBookmarkDialog;